// ════════════════════════════════════════════════════════════════════════
//  API Y SESIÓN — el único sitio del frontend que sabe de token y de usuario
//
//  Antes cada página se apañaba sola: tres copias del mismo `authHeader`, dos
//  `JSON.parse(localStorage...)` sin red y ningún sitio donde poner el "si el
//  servidor dice 401, cierra la sesión". Todo eso vive aquí ahora, así que
//  cambiarlo es cambiar UN archivo.
// ════════════════════════════════════════════════════════════════════════
import axios from 'axios';
import { getLang, t } from './i18n';

// URL base del backend.
// Por defecto es '' (vacío) => las peticiones son RELATIVAS al mismo origen
// que sirve la página. Así el MISMO código funciona en:
//   - local  (FastAPI sirve el build en http://localhost:8000)
//   - ngrok  (https://...ngrok-free.dev)
//   - AWS    (http(s)://tu-dominio-o-ip)
// Sin tener que cambiar nada de código.
//
// Si algún día necesitas apuntar a un backend en OTRO dominio, basta con
// crear un archivo .env con:  REACT_APP_API_URL=https://mi-backend.com
const API = process.env.REACT_APP_API_URL || '';

// Claves de localStorage. Escritas una sola vez para que no haya un
// 'dragonActiveTestId' mal tecleado en algún rincón.
export const STORAGE_KEYS = {
  token: 'token',
  user: 'user',
  testCompleted: 'dragonTestCompleted',
  activeTestId: 'dragonActiveTestId',
  visualMode: 'dragonVisualMode',
  vizStyle: 'dragonVizStyle',
  micMonitor: 'dragonMicMonitor',
};

// ── Sesión ────────────────────────────────────────────────────────────────

export const getToken = () => localStorage.getItem(STORAGE_KEYS.token) || '';

/** Cabecera de autenticación. Úsala como `{ headers: authHeader() }`.
 *
 *  Se lee del localStorage en cada llamada, no una vez al cargar el módulo: si
 *  el usuario entra en otra pestaña, la siguiente petición ya usa el token
 *  nuevo sin tener que recargar la página. */
export const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

/** El usuario guardado, o {} si no hay o está corrupto.
 *
 *  El try/catch NO es paranoia: `JSON.parse` de una cadena inválida LANZA, y
 *  esto se llama en el cuerpo de un componente. Una entrada rota en el
 *  localStorage —una extensión, una sesión a medio escribir, un valor de una
 *  versión vieja— dejaba la página entera en blanco, sin nada en pantalla que
 *  explicara por qué. Con esto, lo peor que pasa es que no salga el nombre. */
export function getStoredUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.user);
    const user = raw ? JSON.parse(raw) : null;
    return user && typeof user === 'object' ? user : {};
  } catch (_) {
    return {};
  }
}

/** Guarda la sesión tras un login correcto. */
export function saveSession(token, user) {
  localStorage.setItem(STORAGE_KEYS.token, token);
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
}

/** Mezcla cambios en el usuario guardado (nombre o correo recién editados en
 *  el perfil), sin tocar el token. */
export function updateStoredUser(patch) {
  const user = { ...getStoredUser(), ...patch };
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(user));
  return user;
}

/** Borra TODO lo de la sesión (cerrar sesión, o token vencido).
 *
 *  Se borran también las preferencias del reproductor a propósito: en un equipo
 *  compartido, el siguiente en entrar no debería heredar el perfil de parlantes
 *  ni el test del anterior. */
export function clearSession() {
  Object.values(STORAGE_KEYS).forEach((k) => localStorage.removeItem(k));
}

// ── Sesión vencida → volver al login ──────────────────────────────────────
//
// Desde que los tokens caducan (backend/app/routes/auth.py), un 401 tiene un
// significado nuevo: "tu sesión se acabó". Sin esto, la app se quedaría en
// pantalla pareciendo funcionar pero sin traer nada — las llamadas fallan en
// silencio dentro de sus try/catch y el usuario ve una lista vacía en vez de
// entender que tiene que volver a entrar.
//
// Se instala UNA vez, aquí, porque este módulo lo importa todo el que llama al
// backend. Ninguna página tiene que acordarse de nada.
const RUTA_LOGIN = '/';

// Las rutas de /auth/ contestan 401/400/429 cuando las credenciales están mal,
// cuando el enlace de recuperación caducó o cuando hubo demasiados intentos. Eso
// NO es una sesión vencida: es la respuesta normal del formulario, y recargar la
// página se llevaría por delante el mensaje de error justo cuando el usuario
// necesita leerlo.
//
// Vale con mirar el prefijo porque NINGUNA ruta de /auth/ requiere sesión: son
// todas puertas de entrada (entrar, registrarse, recuperar contraseña).
const esRutaDeEntrada = (url = '') => url.includes('/auth/');

// El servidor contesta errores y correos en el idioma de la página.
axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers['Accept-Language'] = getLang();
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response && error.response.status;
    const url = (error.config && error.config.url) || '';
    if (status === 401 && !esRutaDeEntrada(url)) {
      clearSession();
      // `replace` y no `href`: la página vencida no se queda en el historial,
      // así que el botón "atrás" no devuelve a una pantalla que ya no funciona.
      if (window.location.pathname !== RUTA_LOGIN) window.location.replace(RUTA_LOGIN);
    }
    return Promise.reject(error);
  }
);

export default API;

// ── Mensajes de error del servidor ────────────────────────────────────────
//
// El backend ya escribe mensajes pensados para que los lea una persona ("Espera
// 15 minutos", "Este enlace ya no sirve"). Antes el frontend los tiraba a la
// basura y enseñaba siempre un texto fijo, así que alguien bloqueado por
// intentos veía "Credenciales incorrectas" y seguía probando su contraseña —
// que era la correcta— sin entender nada.
//
// Esto saca el texto de las DOS formas en que puede venir:
//   · errores de la app  → detail es un texto
//   · errores de validación de FastAPI (422) → detail es una lista de objetos,
//     y el mensaje útil viene con un "Value error, " pegado delante que no
//     significa nada para quien lo lee.
export function mensajeDeError(error, porDefecto = t('common.error.generic')) {
  const detalle = error && error.response && error.response.data && error.response.data.detail;

  if (typeof detalle === 'string' && detalle.trim()) return detalle;

  if (Array.isArray(detalle) && detalle.length) {
    const msg = detalle[0] && detalle[0].msg;
    if (typeof msg === 'string' && msg.trim()) return msg.replace(/^Value error,\s*/, '');
  }

  // Sin respuesta del servidor: no llegó a contestar. Es un problema de red, y
  // decirlo así evita que el usuario culpe a su contraseña.
  if (error && error.request && !error.response) {
    return t('common.error.network');
  }

  return porDefecto;
}
